/**
 * Watch history — last items the user played, scoped to the active profile.
 *
 * v1.3.82 — Added `position_s` + `duration_s` for exact resume.
 * v1.4.30 — Series aggregation + "at least 2 minutes left" filter for
 *           the Continue Watching row. When a series episode is stored
 *           we also record `series_id`, `series_name`, `season`,
 *           `episode` so `getContinueWatching()` can collapse the
 *           episode-level rows into one show-level card (one card per
 *           show, showing the latest-watched episode's progress).
 *
 * Storage stays in localStorage (per-profile key). Old items missing
 * the new fields are treated as movies (backwards-compatible — no
 * migration needed).
 */
import { getActiveProfileId } from "./profiles";
import { scheduleProfileCacheSync } from "./profileCacheSync";
import { markWatched, isWatched } from "./watchedItems";

const BASE_KEY = "vupp_watch_history";
// v1.4.30 — bumped from 12 → 24 because Continue Watching now collapses
// series episodes into one entry per show. Historically 12 items would
// fit ~8 movies + 4 series episodes; with per-show aggregation the same
// space can hold many more distinct titles.
const MAX_ITEMS = 24;

// Positions below this many seconds are considered "just started" —
// no point offering resume for the opening credits.
const MIN_RESUMABLE_SECONDS = 30;

// v1.4.61 — User-facing rule (per product owner): keep an item in the
// Continue Watching row as long as MORE than one minute is still left.
// Only once the remaining playback is <= 60 s do we consider it "done" —
// at that point `saveHistoryPosition` also flips the tile's ✓ overlay
// via `markWatched`. Previously this was 120 s and the item was marked
// watched the instant the player opened, so a 3-second peek showed the
// tile as "watched" and hid it from Continue Watching immediately.
const MIN_REMAINING_SECONDS = 60;

function storageKey() {
  const pid = getActiveProfileId();
  return pid ? `${BASE_KEY}__${pid}` : BASE_KEY;
}

export function getHistory() {
  try {
    const raw = localStorage.getItem(storageKey());
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

/**
 * Look up the resumable position for a stream URL. Returns 0 when the
 * user hasn't started it, has finished it, or is within the "just
 * started" window. Callers should pass this into the player's seek
 * on load.
 */
export function getResumePosition(stream_url) {
  if (!stream_url) return 0;
  const item = getHistory().find((it) => it.stream_url === stream_url);
  if (!item) return 0;
  const pos = Number(item.position_s || 0);
  const dur = Number(item.duration_s || 0);
  if (!Number.isFinite(pos) || pos < MIN_RESUMABLE_SECONDS) return 0;
  // v1.4.61 — Align "finished" definition with the Continue Watching
  // filter: if <= 60 s remain, the item is effectively done and should
  // restart from the beginning if the user re-opens it. This replaces
  // the old 95%-of-runtime heuristic, which was inconsistent with the
  // new user-facing rule ("continue watching while > 1 min remains").
  if (dur > 0 && dur - pos <= MIN_REMAINING_SECONDS) return 0;
  return pos;
}

/**
 * Sugar around `getResumePosition` that returns the whole item so the
 * UI can also render the progress bar underneath the Continue
 * Watching tile ("42% watched — 38 min left").
 */
export function getHistoryItem(stream_url) {
  if (!stream_url) return null;
  return getHistory().find((it) => it.stream_url === stream_url) || null;
}

/**
 * v1.4.30 — Accepts optional series metadata (`series_id`,
 * `series_name`, `season`, `episode`) so `getContinueWatching()` can
 * aggregate episodes under one show card. Old callers that only pass
 * the base four fields still work unchanged.
 */
export function addToHistory({
  stream_url,
  name,
  logo_url,
  content_type,
  series_id,
  series_name,
  season,
  episode,
  poster_url,
} = {}) {
  if (!stream_url || !name) return;
  const existing = getHistory().find((it) => it.stream_url === stream_url);
  const list = getHistory().filter((it) => it.stream_url !== stream_url);
  list.unshift({
    stream_url,
    name,
    logo_url: logo_url || null,
    content_type: content_type || null,
    position_s: existing?.position_s || 0,
    duration_s: existing?.duration_s || 0,
    series_id: series_id ?? existing?.series_id ?? null,
    series_name: series_name ?? existing?.series_name ?? null,
    season: season ?? existing?.season ?? null,
    episode: episode ?? existing?.episode ?? null,
    poster_url: poster_url ?? existing?.poster_url ?? null,
    last_played_at: new Date().toISOString(),
  });
  localStorage.setItem(storageKey(), JSON.stringify(list.slice(0, MAX_ITEMS)));
  scheduleProfileCacheSync();
}

export function saveHistoryPosition(stream_url, position_s, duration_s) {
  if (!stream_url) return;
  const pos = Math.max(0, Math.floor(Number(position_s) || 0));
  const dur = Math.max(0, Math.floor(Number(duration_s) || 0));
  if (pos < 3) return;
  const list = getHistory();
  const idx = list.findIndex((it) => it.stream_url === stream_url);
  if (idx < 0) return;
  const prev = list[idx];
  if (Math.abs((prev.position_s || 0) - pos) < 3 && (prev.duration_s || 0) === dur) return;
  list[idx] = {
    ...prev,
    position_s: pos,
    duration_s: dur,
    last_played_at: new Date().toISOString(),
  };
  try {
    localStorage.setItem(storageKey(), JSON.stringify(list));
  } catch { /* quota exceeded — ignore */ }
  scheduleProfileCacheSync();

  // v1.4.61 — Auto-mark watched when the user is within
  // MIN_REMAINING_SECONDS (60 s) of the end. Below that the item drops
  // out of Continue Watching and the tile picks up the ✓ overlay. Any
  // watch progress that leaves more than a minute remaining keeps the
  // item firmly in Continue Watching, matching the product rule:
  // "must say continue watching if there is more than 1 min remaining".
  // Duration can be 0 for live-ish streams — in that case we don't have
  // enough info to auto-mark, so we skip and rely on the manual "Mark
  // as watched" context-menu action.
  if (dur > 0) {
    const remaining = dur - pos;
    if (remaining <= MIN_REMAINING_SECONDS) {
      markWatched(stream_url, prev.content_type);
    }
  }
}

export function removeFromHistory(stream_url) {
  const list = getHistory().filter((it) => it.stream_url !== stream_url);
  localStorage.setItem(storageKey(), JSON.stringify(list));
  scheduleProfileCacheSync();
}

/**
 * v1.4.30 — Remove every history row belonging to a show. Used by the
 * Continue Watching "×" quick-remove button and by the "Mark show as
 * watched" context-menu action.
 */
export function removeShowFromHistory({ series_id, series_name } = {}) {
  const sid = series_id ? String(series_id) : null;
  const sname = series_name ? String(series_name).toLowerCase() : null;
  const list = getHistory().filter((it) => {
    if ((it.content_type || "") !== "series") return true;
    if (sid && String(it.series_id || "") === sid) return false;
    if (sname && String(it.series_name || "").toLowerCase() === sname) return false;
    return true;
  });
  localStorage.setItem(storageKey(), JSON.stringify(list));
  scheduleProfileCacheSync();
}

export function clearHistory() {
  localStorage.removeItem(storageKey());
  scheduleProfileCacheSync();
}

/**
 * v1.4.30 — Continue Watching row for the given tab type.
 * See top-of-file doc block for aggregation + filter semantics.
 */
export function getContinueWatching(type) {
  const wantType = String(type || "").toLowerCase();
  const list = getHistory();

  if (wantType === "movie" || wantType === "catchup") {
    return list
      .filter((it) => (it.content_type || "") === wantType)
      .filter((it) => hasEnoughLeft(it))
      // v1.4.61 — hide items the user (or the auto-mark heuristic) has
      // flagged as watched, even if enough runtime is technically left.
      .filter((it) => !isWatched(it.stream_url))
      .map((it) => ({ kind: "movie", item: it }));
  }

  if (wantType === "series") {
    const byShow = new Map();
    for (const it of list) {
      if ((it.content_type || "") !== "series") continue;
      const key = keyForSeriesRow(it);
      if (!key) continue;
      if (byShow.has(key)) continue; // newest-first — keep the first hit
      if (!hasEnoughLeft(it)) continue; // drop show when latest ep near end
      if (isWatched(it.stream_url)) continue; // v1.4.61 — manually marked
      byShow.set(key, {
        kind: "series",
        series_id: it.series_id || null,
        series_name: it.series_name || it.name,
        poster_url: it.poster_url || it.logo_url || null,
        logo_url: it.logo_url || null,
        latest: {
          stream_url: it.stream_url,
          name: it.name,
          season: it.season ?? null,
          episode: it.episode ?? null,
          position_s: it.position_s || 0,
          duration_s: it.duration_s || 0,
          last_played_at: it.last_played_at,
        },
      });
    }
    return Array.from(byShow.values());
  }

  return [];
}

function keyForSeriesRow(row) {
  if (row.series_id) return `id:${row.series_id}`;
  if (row.series_name) return `name:${row.series_name.toLowerCase()}`;
  return row.name ? `name:${row.name.toLowerCase()}` : null;
}

function hasEnoughLeft(item) {
  const pos = Number(item.position_s || 0);
  const dur = Number(item.duration_s || 0);
  if (!Number.isFinite(dur) || dur <= 0) return true;
  const left = dur - pos;
  return left >= MIN_REMAINING_SECONDS;
}
